import React, { useState, useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { Mail, MailCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { apiErrorMessage } from '../utils/helpers';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const RESEND_COOLDOWN_SECONDS = 60;

// Login sin contraseña: se pide el correo y se envía un enlace de un solo uso
const Login = () => {
  const { isAuthenticated, loading } = useAuth();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownRef = useRef(null);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    cooldownRef.current = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(cooldownRef.current);
  }, [resendCooldown]);

  const requestLink = async () => {
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/login-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await response.json();
      if (response.ok) {
        setLinkSent(true);
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
      } else if (response.status === 429) {
        // Ya se mandó uno hace menos de un minuto (p. ej. desde el popup): ese sigue sirviendo
        setLinkSent(true);
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
        toast.info('Ya te enviamos un enlace hace un momento, revisa tu correo');
      } else {
        toast.error(apiErrorMessage(data, 'No se pudo enviar el enlace'));
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    requestLink();
  };

  const openRegister = () => {
    window.dispatchEvent(new CustomEvent('openWelcomePopup'));
  };

  if (!loading && isAuthenticated) {
    return <Navigate to="/profile" replace />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-20 bg-brand-bone" data-testid="login-page">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-luxury-lg p-8 border border-brand-black/10">
        {linkSent ? (
          <div className="text-center" data-testid="login-link-sent">
            <div
              className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #1B4FD8, #C2185B)' }}
            >
              <MailCheck size={32} className="text-white" />
            </div>
            <h1 className="font-display text-3xl tracking-tight font-light mb-2 text-brand-black">
              Revisa tu correo
            </h1>
            <p className="text-brand-black/60 text-sm mb-6">
              Te enviamos un enlace a <span className="font-semibold text-brand-black">{email}</span>.
              Ábrelo en este dispositivo para entrar; tu sesión quedará guardada aquí.
            </p>
            <button
              type="button"
              onClick={requestLink}
              disabled={isSubmitting || resendCooldown > 0}
              className="text-sm font-medium text-brand-cobalt hover:underline disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {resendCooldown > 0
                ? `Reenviar enlace (${resendCooldown}s)`
                : isSubmitting
                  ? 'Reenviando...'
                  : '¿No te llegó? Reenviar enlace'}
            </button>
            <p className="mt-4">
              <button
                type="button"
                onClick={() => setLinkSent(false)}
                className="text-xs text-brand-black/50 hover:underline"
              >
                Usar otro correo
              </button>
            </p>
          </div>
        ) : (
          <>
            <h1 className="font-display text-3xl tracking-tight font-light text-center mb-2 text-brand-black">
              Iniciar Sesión
            </h1>
            <p className="text-center text-brand-black/50 text-sm mb-8">
              Te enviamos un enlace a tu correo para entrar, sin contraseña
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-cobalt/50" />
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@email.com"
                  autoComplete="email"
                  required
                  className="w-full pl-10 pr-4 py-3 rounded-lg text-brand-black placeholder:text-brand-black/30 border border-brand-black/20 focus:outline-none focus:border-brand-cobalt transition-colors"
                  data-testid="login-email-input"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3 text-white font-bold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                style={{ background: 'linear-gradient(90deg, #1B4FD8, #C2185B)' }}
                data-testid="login-submit-btn"
              >
                {isSubmitting ? 'Enviando...' : 'Enviarme el enlace'}
              </button>
            </form>

            <p className="text-center text-brand-black/50 text-sm mt-6">
              ¿No tienes cuenta?{' '}
              <button
                type="button"
                onClick={openRegister}
                className="text-brand-cobalt font-medium hover:underline"
                data-testid="login-open-register"
              >
                Regístrate
              </button>
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default Login;
